import * as React from 'react';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react-lite';
import { Layout, Typography, Radio, Row, Col, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { AuthConsumer } from '../../authorization/AuthContext';
import { STORE_ML_MODELS_MANAGEMENT } from '../constants';
import { AdministrationStores } from '../stores';
import FlexContentContainer from '../../common/components/FlexContentContainer';
import MlModelsTable from '../components/MlModelsTable/MlModelsTable';
import LayoutHeader from '../../../components/LayoutHeader';
import { timeOptions, defaultIntervalTime } from '../stores/MlModelsManagementStore';

type Props = AdministrationStores;

const MlModelsManagementPage: React.FC<Props> = ({ MlModelsManagement: store }) => {
    // Initialize store on mount and cleanup on unmount
    React.useEffect(() => {
        if (store) {
            store.initialize();
        }

        return () => {
            if (store) {
                store.dispose();
            }
        };
    }, [store]);

    if (!store) {
        return null;
    }

    const content = (
        <AuthConsumer>
            {() => (
                <Layout>
                    <LayoutHeader title="ML Models Management" />

                    <Layout.Content>
                        {/* Refresh interval selector and manual refresh button */}
                        <Row style={{ marginBottom: 24 }} gutter={16} align="bottom">
                            <Col>
                                <Typography.Paragraph style={{ fontWeight: 500, fontSize: 16, marginBottom: 8 }}>
                                    Refresh interval
                                </Typography.Paragraph>

                                <Radio.Group
                                    onChange={e => {
                                        store.setFetchIntervalTime(e.target.value);
                                    }}
                                    defaultValue={defaultIntervalTime}
                                    buttonStyle="solid"
                                >
                                    {timeOptions.map(option => (
                                        <Radio.Button key={option.value} value={option.value}>
                                            {option.label}
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </Col>
                            <Col>
                                {/* Manual refresh button to immediately fetch latest models */}
                                <Button
                                    type="default"
                                    icon={<ReloadOutlined />}
                                    onClick={() => store.getLoadedModels()}
                                    loading={store.loading}
                                >
                                    Refresh Now
                                </Button>
                            </Col>
                        </Row>

                        {/* ML Models Table */}
                        <Row>
                            <Col span={24}>
                                <MlModelsTable store={store} />
                            </Col>
                        </Row>
                    </Layout.Content>
                </Layout>
            )}
        </AuthConsumer>
    );

    return <FlexContentContainer content={content} contentStyle={{ overflow: 'hidden' }} />;
};

export default inject(STORE_ML_MODELS_MANAGEMENT)(observer(MlModelsManagementPage));
